import type { HeroClass, SkillId } from './heroes.ts';
import type { EnemyKind, NightmareType } from './nightmares.ts';
import type { GlyphId } from './glyphs.ts';
import type { GearAttr, GearEffect, UniqueSlot } from './progression.ts';
import type { Resources } from './structures.ts';
import { sec } from './units.ts';

/**
 * The saga of the Turtle (docs/QUESTS.md, lore in docs/LORE.md): seven hand-written chapters, one at a
 * time for the group, each opened by a Marea. Acts are scenes (a character, a few lines, a choice) or
 * battles; the story is fixed, the battles are rolled anew on every try. Texts live in i18n under
 * `saga.<chapter>.<act>.<line>` and `saga.<chapter>.<act>.c.<choice>` (+ `.desc`).
 */

/** The saga is open (off, the server refuses saga acts and the client hides the saga; tests turn it off and on). */
export const SAGA_SWITCH = { on: true };

export type NpcId = 'brann' | 'maelis' | 'odna' | 'hilde' | 'gaspar' | 'fenn' | 'mada' | 'ulla' | 'cal' | 'orla' | 'dorn' | 'pell' | 'yorra';
export const NPCS: NpcId[] = ['brann', 'maelis', 'odna', 'hilde', 'gaspar'];

/** What a line or a choice needs: a mark set (or not), an affinity at least (or below) this. */
export interface SagaCond {
  flag?: string;
  notFlag?: string;
  aff?: [NpcId, number];
  affBelow?: [NpcId, number];
}

export interface SagaLine {
  id: string;
  who: NpcId | 'narrator';
  if?: SagaCond;
}

export interface SagaChoice {
  id: string;
  /** Affinity changes (clamped to ±3). */
  aff?: Partial<Record<NpcId, number>>;
  /** Marks it sets, for later lines, choices and the last chapter. */
  set?: string[];
  /** Provisions it gives (or takes). */
  provisions?: number;
  /** What it costs from the common storage. */
  cost?: Partial<Resources>;
  if?: SagaCond;
}

export interface SagaScene {
  kind: 'scene';
  id: string;
  lines: SagaLine[];
  choices: SagaChoice[];
}

/**
 * A battle act. `hold`: an incursion clearing held until the bar fills; `escort`: a caravan led across;
 * `descent`: crypt rooms (a boss at the end if given); `boss`: a clearing and the chapter's boss.
 */
export interface SagaBattle {
  kind: 'battle';
  id: string;
  mode: 'hold' | 'escort' | 'descent' | 'boss';
  /** The lay of the land (incursion maps): which twist the clearing plays with. */
  land?: 'quarry' | 'forest' | 'crystal' | 'altar' | 'burnClearing';
  /** Nightmares: × the incursion budget (hold, escort, boss adds) or × the crypt's room points (descent). */
  budgetBp: number;
  /** Descent: rooms before the end, and what lives in them (weights). */
  rooms?: number;
  bestiary?: Partial<Record<NightmareType, number>>;
  /** The chapter's boss: a kind, and its health by party size (1 to 4; past 4, BIG_PARTY) before level scaling. */
  boss?: { kind: EnemyKind; tier: 1 | 2 | 3; hp: number[] };
  /** Share of inked nightmares. */
  inkBp?: number;
  /** Escort: the caravan's life × this (bp). */
  cartHpBp?: number;
  /** A party of four meets tougher nightmares here (bp of their life, or of the rooms' or the boss's): some acts are easier for many. */
  fourBp?: number;
}

export type SagaAct = SagaScene | SagaBattle;

export interface SagaChapter {
  id: string;
  /**
   * Opens, once the chapter before is done, with the group at this level (the active heroes' average) and the
   * bastion in this era (docs/PROGRESION.md): the story walks with the heroes, not the calendar (it waited for a
   * Marea until 2026-10-28). A chapter already begun never closes again.
   */
  minGroup?: number;
  minEra?: number;
  /** The hero's level to take its unique piece; a hero below it gets it on reaching it. */
  uniqueLevel?: number;
  acts: SagaAct[];
  /** The unique piece it gives everyone who played an act. */
  unique?: UniqueSlot;
  /**
   * A whole glyph it gives everyone who played an act, once per hero (docs/UNICAS.md §1): the Turtle 4 and 6 stopped
   * giving a unique piece on 2026-11-02. A hero who has it already gets another conversion glyph it lacks; with all of
   * them, three fragments of any.
   */
  glyph?: GlyphId;
  /** Running out of provisions: what the story loses (from the storage), and the mark it leaves. */
  loss: { storage?: Partial<Resources>; aff?: Partial<Record<NpcId, number>>; set: string };
  /** The last chapter pays much more. */
  final?: boolean;
  /**
   * Its nightmares and boss have this much life (bp): from the third chapter on the party wears the
   * unique pieces of the chapters before, and the saga is measured with them on.
   */
  toughBp?: number;
}

const line = (id: string, who: SagaLine['who'], cond?: SagaCond): SagaLine => (cond ? { id, who, if: cond } : { id, who });
const go: SagaChoice = { id: 'go' };

export const SAGA_CHAPTERS: SagaChapter[] = [
  {
    id: 'garrison',
    toughBp: 12000,
    minGroup: 3,
    loss: { storage: { wood: 120 }, aff: { odna: -1 }, set: 'lost.caravan' },
    acts: [
      {
        kind: 'scene',
        id: 'odna',
        lines: [line('l1', 'narrator'), line('l2', 'odna'), line('l3', 'odna'), line('l5', 'odna')],
        choices: [
          { id: 'pay', cost: { stone: 120, crystal: 40 }, aff: { odna: 1 }, set: ['caravan.paid'] },
          { id: 'ask', aff: { odna: -1 }, provisions: 1, set: ['caravan.asked'] },
        ],
      },
      { kind: 'battle', id: 'caravan', mode: 'escort', land: 'forest', budgetBp: 17000, fourBp: 13000 },
      {
        kind: 'scene',
        id: 'brann',
        lines: [line('l1', 'narrator'), line('l2', 'brann'), line('l3', 'brann'), line('l4', 'odna', { flag: 'caravan.paid' }), line('l5', 'odna', { flag: 'caravan.asked' }), line('l6', 'brann')],
        choices: [
          { id: 'welcome', aff: { brann: 1 } },
          { id: 'press', aff: { brann: -1 }, set: ['asked.brann'] },
        ],
      },
    ],
  },
  {
    id: 'wall',
    toughBp: 14200,
    minGroup: 4,
    uniqueLevel: 5,
    unique: 'weapon',
    loss: { aff: { brann: -1 }, set: 'lost.armory' },
    acts: [
      {
        kind: 'scene',
        id: 'ruins',
        lines: [line('l1', 'narrator'), line('l2', 'brann'), line('l3', 'brann'), line('l4', 'brann', { flag: 'asked.brann' }), line('l5', 'brann', { notFlag: 'asked.brann' })],
        choices: [go],
      },
      { kind: 'battle', id: 'barracks', mode: 'descent', budgetBp: 13000, rooms: 3, bestiary: { skeleton: 4, wraith: 2, goblin: 2 }, inkBp: 2000, fourBp: 9500 },
      {
        kind: 'scene',
        id: 'gaspar',
        lines: [line('l1', 'narrator'), line('l2', 'narrator'), line('l3', 'brann'), line('l4', 'gaspar'), line('l5', 'gaspar'), line('l6', 'brann')],
        choices: [
          { id: 'listen', aff: { gaspar: 1, brann: -1 }, set: ['gaspar.heard'] },
          { id: 'leave', aff: { brann: 1, gaspar: -1 } },
        ],
      },
      { kind: 'battle', id: 'captain', mode: 'boss', land: 'quarry', budgetBp: 6000, boss: { kind: 'skeleton', tier: 3, hp: [0, 1800, 2700, 4300, 6200] }, inkBp: 3000, fourBp: 9500 },
      {
        kind: 'scene',
        id: 'armory',
        lines: [
          line('l1', 'narrator'),
          line('l2', 'gaspar', { flag: 'gaspar.heard' }),
          line('l3', 'gaspar', { flag: 'gaspar.heard' }),
          line('l4', 'narrator', { notFlag: 'gaspar.heard' }),
          line('l5', 'narrator'),
          line('l6', 'brann'),
        ],
        choices: [{ id: 'take' }],
      },
    ],
  },
  {
    id: 'fire',
    toughBp: 20800,
    minGroup: 6,
    minEra: 2,
    loss: { storage: { wood: 150, stone: 80 }, aff: { odna: -1 }, set: 'lost.houses' },
    acts: [
      { kind: 'scene', id: 'dreams', lines: [line('l1', 'odna'), line('l2', 'odna'), line('l3', 'odna')], choices: [go] },
      { kind: 'battle', id: 'house', mode: 'hold', land: 'altar', budgetBp: 18000, inkBp: 3500 },
      {
        kind: 'scene',
        id: 'drawing',
        lines: [line('l1', 'narrator'), line('l2', 'brann'), line('l3', 'brann'), line('l4', 'odna')],
        choices: [
          { id: 'burn', aff: { odna: 1 }, set: ['house.burned'] },
          { id: 'keep', aff: { odna: -1 }, set: ['house.kept'] },
        ],
      },
    ],
  },
  {
    id: 'deep',
    toughBp: 26000,
    minGroup: 9,
    glyph: 'storm',
    loss: { aff: { hilde: -1 }, set: 'hilde.hurt' },
    acts: [
      { kind: 'scene', id: 'books', lines: [line('l1', 'hilde'), line('l2', 'hilde'), line('l3', 'hilde')], choices: [go] },
      { kind: 'battle', id: 'halls', mode: 'descent', budgetBp: 17500, rooms: 4, bestiary: { skeleton: 3, gargoyle: 2, slime: 2, stalker: 2 }, inkBp: 4000, boss: { kind: 'warden', tier: 1, hp: [0, 3200, 4800, 10500, 20000] }, fourBp: 12000 },
      {
        kind: 'scene',
        id: 'note',
        lines: [line('l1', 'narrator'), line('l2', 'narrator'), line('l3', 'hilde')],
        choices: [
          { id: 'show', aff: { hilde: 1, brann: -1 }, set: ['hilde.knows'] },
          { id: 'hide', set: ['note.hidden'] },
        ],
      },
      {
        kind: 'scene',
        id: 'forge',
        lines: [line('l1', 'narrator'), line('l2', 'hilde', { flag: 'hilde.knows' }), line('l3', 'hilde', { notFlag: 'hilde.knows' }), line('l4', 'hilde')],
        choices: [{ id: 'take' }],
      },
    ],
  },
  {
    id: 'postern',
    toughBp: 15500,
    minGroup: 12,
    minEra: 3,
    loss: { storage: { stone: 150 }, aff: { brann: -1 }, set: 'lost.postern' },
    acts: [
      { kind: 'scene', id: 'door', lines: [line('l1', 'brann', { flag: 'hilde.knows' }), line('l2', 'brann', { notFlag: 'hilde.knows' }), line('l3', 'brann')], choices: [go] },
      { kind: 'battle', id: 'night', mode: 'hold', land: 'quarry', budgetBp: 13500, inkBp: 4500, fourBp: 12000 },
      {
        kind: 'scene',
        id: 'diary',
        lines: [line('l1', 'narrator'), line('l2', 'narrator'), line('l3', 'brann', { aff: ['brann', 1] }), line('l4', 'brann', { aff: ['brann', 1] }), line('l5', 'brann', { affBelow: ['brann', 1] })],
        choices: [
          { id: 'tell', aff: { odna: 2, brann: -1 }, set: ['odna.knows', 'diary'] },
          { id: 'keep', aff: { brann: 2 }, set: ['brann.secret', 'diary'] },
        ],
      },
    ],
  },
  {
    id: 'maelis',
    // 2026-11-02: 24500 with the armour piece worn; it now comes at the dragon (docs/UNICAS.md §5), back to what bots won.
    toughBp: 21000,
    minGroup: 15,
    glyph: 'rime',
    loss: { storage: { wood: 120, stone: 120 }, aff: { odna: -1 }, set: 'lost.fields' },
    acts: [
      { kind: 'scene', id: 'closer', lines: [line('l1', 'odna'), line('l2', 'gaspar', { flag: 'gaspar.heard' }), line('l3', 'brann', { notFlag: 'gaspar.heard' }), line('l4', 'narrator')], choices: [go] },
      { kind: 'battle', id: 'dream', mode: 'descent', budgetBp: 21000, rooms: 3, bestiary: { wraith: 3, slime: 2, stalker: 2, skeleton: 2 }, inkBp: 6000, fourBp: 7000 },
      {
        kind: 'scene',
        id: 'witch',
        lines: [line('l1', 'maelis'), line('l2', 'maelis', { flag: 'house.burned' }), line('l3', 'maelis', { flag: 'house.kept' }), line('l4', 'maelis')],
        choices: [
          { id: 'diary', if: { flag: 'diary' }, aff: { maelis: 2 }, set: ['maelis.knows'] },
          { id: 'fight', aff: { maelis: -1 } },
        ],
      },
      { kind: 'battle', id: 'ashes', mode: 'boss', land: 'altar', budgetBp: 10000, boss: { kind: 'witch', tier: 2, hp: [0, 3800, 5600, 9500, 15000] }, inkBp: 5000, fourBp: 7000 },
      {
        kind: 'scene',
        id: 'remedies',
        lines: [
          line('l1', 'narrator'),
          line('l2', 'maelis', { flag: 'maelis.knows' }),
          line('l3', 'maelis', { flag: 'maelis.knows' }),
          line('l4', 'maelis', { notFlag: 'maelis.knows' }),
          line('l5', 'narrator'),
        ],
        choices: [{ id: 'take' }],
      },
    ],
  },
  {
    id: 'burn',
    toughBp: 26000,
    minGroup: 20,
    // The armour comes from the dragon (docs/UNICAS.md §1): Hilde works the Burn's glass, cold the day it falls.
    uniqueLevel: 20,
    unique: 'armor',
    minEra: 4,
    final: true,
    loss: { storage: { wood: 200, stone: 200, crystal: 100 }, aff: { odna: -1 }, set: 'lost.village' },
    acts: [
      {
        kind: 'scene',
        id: 'sky',
        lines: [line('l1', 'narrator'), line('l2', 'brann'), line('l3', 'odna')],
        choices: [
          { id: 'open', aff: { odna: 2 }, provisions: 1, set: ['gate.open'] },
          { id: 'closed', aff: { odna: -2 }, set: ['gate.closed'] },
        ],
      },
      { kind: 'battle', id: 'flight', mode: 'escort', land: 'quarry', budgetBp: 12000, fourBp: 6000, inkBp: 5000, cartHpBp: 11000 },
      { kind: 'battle', id: 'dragon', mode: 'boss', land: 'burnClearing', budgetBp: 8000, boss: { kind: 'dragon', tier: 3, hp: [0, 9500, 15000, 28000, 47000] }, inkBp: 6000 },
      {
        kind: 'scene',
        id: 'dawn',
        lines: [
          line('l1', 'narrator'),
          // The Burn's glass cold for the first time: Hilde forges the armours from it (docs/UNICAS.md §4.12).
          line('g1', 'narrator'),
          line('g2', 'hilde'),
          line('l2', 'gaspar', { flag: 'gaspar.heard' }),
          line('l3', 'maelis', { flag: 'maelis.knows' }),
          line('l4', 'odna', { aff: ['odna', 2] }),
          line('l5', 'hilde', { aff: ['hilde', 1] }),
          line('l6', 'brann'),
          line('l7', 'narrator'),
        ],
        choices: [{ id: 'end' }],
      },
    ],
  },
];

/** Provisions, scaling and rewards. */
export const SAGA = {
  provisions: 3,
  maxProvisions: 5,
  affinityMax: 3,
  /** Party scaling of battle acts by heroes (1 to 8; past 4, BIG_PARTY) and +10 % per average level above 1. */
  partyBp: [0, 10000, 17500, 37000, 58000],
  /** Hold acts scale less with the party: one node, however many heroes guard it. */
  holdPartyBp: [0, 10000, 15000, 25000, 36000],
  /** Past four heroes (BIG_PARTY), a boss's health grows with the party's blows: one more hero's worth each, no ramp. */
  bigBoss: { extraBp: 10000, rampBp: 0 },
  /** Past four heroes, its nightmares (BIG_PARTY's split): gentler than a crypt's, the node falls before the heroes do. */
  big: { extraBp: 14500, rampBp: 6000 },
  /**
   * 8 % until the class balance (docs/BALANCE_CLASES.md): skills grow with the level, the warrior cleaves and the saga's
   * companions (spectres) fall back in time. 10 %, and each chapter's toughBp raised, keeps every chapter at least as
   * hard as before (saga-*-4p with bots, 200 runs each).
   */
  perLevelBp: 1000,
  /** Every nightmare in a saga battle is this much tougher, and hits this much harder (bp). */
  enemyHpBp: 14000,
  dmgBp: 12000,
  /** Hold and escort acts: as long as an incursion's extraction, three waves. */
  waves: 3,
  /** A chapter done: into the common storage, and XP for everyone who played an act of it. */
  reward: { wood: 320, stone: 320, crystal: 180, dust: 220 } as Resources,
  finalReward: { wood: 900, stone: 900, crystal: 500, dust: 700 } as Resources,
  xp: 800,
  finalXp: 3000,
  /** XP for a won battle act (to the heroes who fought it). */
  actXp: 150,
  /** A decision stays open to the group's votes this long at most (it closes before if everyone voted, or one side has a majority). */
  voteMs: 24 * 3_600_000,
  /**
   * Escort acts: a caravan crosses the clearing from one road to the other through the middle. There is no
   * node: every nightmare comes for it, straight at it from this close and along the ground from farther.
   * It stops while one is this close, moves this much a tick, and has this much life (before level scaling). Its lanterns show the unseen this close.
   */
  escort: { cartHp: 2400, cartPartyBp: 4000, draw: 3000, stopRange: 1200, speed: 26, lantern: 2600 },
  /** A boss act's node holds this much (the boss comes for the heroes; its nightmares go for the node). */
  bossNodeHp: 4000,
};

/**
 * The 24 unique pieces (docs/QUESTS.md): one per class and slot, bound to the hero who earned it (no
 * gifting, melting or tempering). Never worse than a rare piece: three attributes instead of two, and a
 * rare effect of their own at UNIQUE_EFFECT_BP of its strength. On top, each one changes how a skill of
 * the class works, enough to build around; the numbers ride in `fx`. The glass scales of the dragon
 * only crack under blows from heroes who wear them.
 */
export type UniqueId =
  | 'gateBlade' | 'lastWatch' | 'posternMedal'
  | 'garrisonKey' | 'anvilHarness' | 'maelisKit'
  | 'fallenStaff' | 'frostMantle' | 'stormEye'
  | 'chapelLamp' | 'dwarfMail' | 'healerRosary'
  | 'watchBow' | 'wandererCloak' | 'mossWhistle'
  | 'wallAxe' | 'boarHide' | 'tailFang'
  | 'gasparHammer' | 'oathPlate' | 'vigilCandle'
  | 'oldRootStaff' | 'dwarfBark' | 'maelisSeed';

export interface UniqueDef {
  id: UniqueId;
  cls: HeroClass;
  slot: UniqueSlot;
  /** The skill it changes (none: it wakes by itself or rides the basic attack). */
  skill: SkillId | null;
  /** Fixed attributes (bp), stronger than a rare piece's, one more than it. */
  attrs: Partial<Record<GearAttr, number>>;
  /** Its own rare effect, at UNIQUE_EFFECT_BP. */
  effect: GearEffect;
  fx: Record<string, number>;
}

/** A unique's own rare effect is worth this much of a rare piece's (bp). */
export const UNIQUE_EFFECT_BP = 15000;

/**
 * Awakening a unique piece at the forge (docs/PROGRESION.md): four stages, one at a time, on the same piece
 * (its reforges, pluses and their places stay). Each stage asks the hero's level, the forge's level (which the
 * bastion's era opens), one seal earned where only that stage's fight gives it, dream dust from the storage and
 * `rares` rare pieces from the bag melted into it (the drops matter again). Each stage adds `attrStepBp` to every
 * attribute of the piece and `effectStepBp` to its own rare effect.
 * - deep: the deep crypt cleared to its last floor;
 * - furrows: the Furrows' centre won;
 * - tide: a boss of the late Mareas felled;
 * - dragon: the Drowned Dragon felled (La Orilla 5, docs/SAGAS.md).
 */
export type SealId = 'deep' | 'furrows' | 'tide' | 'dragon';
export const SEAL_IDS: SealId[] = ['deep', 'furrows', 'tide', 'dragon'];
export const AWAKEN = {
  stages: [
    { level: 15, forge: 4, seal: 'deep' as SealId, dust: 400, rares: 3 },
    { level: 25, forge: 5, seal: 'furrows' as SealId, dust: 800, rares: 4 },
    { level: 35, forge: 6, seal: 'tide' as SealId, dust: 1400, rares: 5 },
    { level: 45, forge: 6, seal: 'dragon' as SealId, dust: 2000, rares: 6 },
  ],
  attrStepBp: 300,
  effectStepBp: 2500,
  /**
   * The unique's own power grows too: each stage, what makes it stronger by growing (reach, damage, healing,
   * length, stun…) grows `powerStepBp`, and what makes it stronger by shrinking (`lower`: recharge, the wait
   * between pulses, the share of damage taken) shrinks `quickStepBp`. `keep` are thresholds that only mean
   * "when", left as they are.
   */
  powerStepBp: 1500,
  quickStepBp: 800,
  lower: ['cooldownBp', 'every', 'takenBp'],
  keep: ['belowBp', 'to', 'near'],
};

/** A unique's numbers at an awakening stage (0: as forged in the saga). Integers round to the nearest. */
export const awakenedFx = (fx: Record<string, number>, stage: number): Record<string, number> => {
  if (stage <= 0) return fx;
  const out: Record<string, number> = {};
  for (const k of Object.keys(fx).sort()) {
    const v = fx[k] as number;
    if (AWAKEN.keep.includes(k)) out[k] = v;
    else if (AWAKEN.lower.includes(k)) out[k] = Math.trunc((v * (10000 - stage * AWAKEN.quickStepBp) + 5000) / 10000);
    else out[k] = Math.trunc((v * (10000 + stage * AWAKEN.powerStepBp) + 5000) / 10000);
  }
  return out;
};

/** The attributes of a unique piece by slot; the third came on 2026-10-26 (older pieces get it when the campaign loads). */
export const UNIQUE_ATTRS: Record<UniqueSlot, Partial<Record<GearAttr, number>>> = {
  weapon: { damage: 1800, attackSpeed: 1200, crit: 1500 },
  armor: { hp: 1800, armor: 1000, cooldown: 1200 },
  amulet: { cooldown: 1500, damage: 1000, hp: 1500 },
};

const u = (id: UniqueId, cls: HeroClass, slot: UniqueSlot, skill: SkillId | null, effect: GearEffect, fx: Record<string, number> = {}): UniqueDef => ({
  id,
  cls,
  slot,
  skill,
  attrs: UNIQUE_ATTRS[slot],
  effect,
  fx,
});

/** `cooldownBp` / `durationBp`: the skill it changes recharges / lasts this much of its own (bp). */
export const UNIQUES: Record<UniqueId, UniqueDef> = {
  /**
   * The shield bash hits everything within `radius` of its target, `damageBp` as hard, and stuns the big
   * ones whole and elites by a third (bosses never); the warrior's blows hit the stunned `stunnedBp` harder.
   */
  gateBlade: u('gateBlade', 'warrior', 'weapon', 'shieldBash', 'cleave', { radius: 1500, damageBp: 20000, stunnedBp: 5000 }),
  /** While the challenge lasts, every melee blow taken strikes back for `attackBp` of the warrior's attack and heals `healBp` of its life. */
  lastWatch: u('lastWatch', 'warrior', 'armor', 'challenge', 'thorns', { attackBp: 10000, healBp: 150 }),
  /** While the oath lasts, no ally it reaches can fall below 1 life; it raises the fallen at `hpBp`. */
  posternMedal: u('posternMedal', 'warrior', 'amulet', 'oath', 'guard', { hpBp: 5000 }),
  /** Turrets this close to each other are joined by a beam that burns whoever crosses it; one turret more. */
  garrisonKey: u('garrisonKey', 'artificer', 'weapon', 'turret', 'chain', { link: 3500, width: 450, damage: 20, every: 6, extra: 1 }),
  /**
   * The automaton walks with the artificer, lasts longer and bursts when it breaks. It holds its ground until
   * its maker is farther than `leash`, then walks back to `near`: the artificer can stand a few cells behind the fight.
   */
  anvilHarness: u('anvilHarness', 'artificer', 'armor', 'automaton', 'guard', { radius: 1800, damage: 180, leash: 3500, near: 2200, durationBp: 20000 }),
  /** The grenade heals the allies it reaches and recharges sooner. */
  maelisKit: u('maelisKit', 'artificer', 'amulet', 'gearGrenade', 'echo', { heal: 90, cooldownBp: 6000 }),
  /** The fireball leaps on to `chains` more targets nearby. */
  fallenStaff: u('fallenStaff', 'mage', 'weapon', 'fireball', 'burn', { chains: 4, range: 3000, damageBp: 8000 }),
  /** Low on life, a ring of ice rises around the mage: the nightmares in it freeze and the mage can't be hurt a moment. */
  frostMantle: u('frostMantle', 'mage', 'armor', null, 'frost', { belowBp: 4000, radius: 1600, slowBp: 6000, ticks: 48, every: 240, stun: 24, invuln: 24 }),
  /** The storm follows the mage and lasts longer. */
  stormEye: u('stormEye', 'mage', 'amulet', 'storm', 'echo', { durationBp: 20000 }),
  /** The basic attack heals the most hurt ally nearby by a share of the blow. */
  chapelLamp: u('chapelLamp', 'cleric', 'weapon', null, 'lifesteal', { range: 4000, healBp: 10000 }),
  /** The sanctuary moves with the cleric, recharges sooner and those in it take less. */
  dwarfMail: u('dwarfMail', 'cleric', 'armor', 'sanctuary', 'guard', { cooldownBp: 6000, takenBp: 7500 }),
  /** Dawn reaches the whole party, wherever they are, and comes back sooner. */
  healerRosary: u('healerRosary', 'cleric', 'amulet', 'dawn', 'echo', { cooldownBp: 6000 }),
  /** Every third arrow goes through the line, `damageBp` as hard, and marks: the marked take more. */
  watchBow: u('watchBow', 'ranger', 'weapon', null, 'execute', { every: 3, length: 6000, width: 500, markBp: 13000, markTicks: 48, damageBp: 20000 }),
  /** After the net, a leap back; the net recharges sooner. */
  wandererCloak: u('wandererCloak', 'ranger', 'armor', 'net', 'swift', { leap: 2500, cooldownBp: 5000 }),
  /** The arrow rain reveals the unseen and slows, and lasts and hits more. */
  mossWhistle: u('mossWhistle', 'ranger', 'amulet', 'arrowRain', 'chain', { slowBp: 5000, durationBp: 15000, damageBp: 15000 }),
  /** The whirl pulls nightmares in (elites too) and recharges sooner. */
  wallAxe: u('wallAxe', 'barbarian', 'weapon', 'whirl', 'cleave', { radius: 2600, to: 700, cooldownBp: 7000 }),
  /** Low on life, the frenzy starts by itself (once every `every`). */
  boarHide: u('boarHide', 'barbarian', 'armor', 'frenzy', 'fury', { belowBp: 3500, every: 540 }),
  /** A leap that kills is ready again, and each nightmare it hits gives back `healBp` of the barbarian's life. */
  tailFang: u('tailFang', 'barbarian', 'amulet', 'leap', 'lifesteal', { healBp: 400 }),
  /** The smite stuns (the big ones too) and recharges sooner. */
  gasparHammer: u('gasparHammer', 'paladin', 'weapon', 'smite', 'execute', { stun: 24, cooldownBp: 7000 }),
  /** The aegis covers the paladin too, holds more, and taunts when it breaks. */
  oathPlate: u('oathPlate', 'paladin', 'armor', 'aegis', 'thorns', { taunt: 36, radius: 2500, amountBp: 15000 }),
  /** The hallowed ground moves with the paladin and lasts longer. */
  vigilCandle: u('vigilCandle', 'paladin', 'amulet', 'hallow', 'banner', { durationBp: 20000 }),
  /** Roots spread to the nightmares near the rooted ones and hold longer. */
  oldRootStaff: u('oldRootStaff', 'druid', 'weapon', 'entangle', 'frost', { radius: 1400, holdBp: 15000 }),
  /** The bear lasts longer, and whoever strikes it takes `attackBp` of the druid's attack back. */
  dwarfBark: u('dwarfBark', 'druid', 'armor', 'bearForm', 'thorns', { attackBp: 10000, durationBp: 15000 }),
  /** The bloom walks with the druid and lasts longer. */
  maelisSeed: u('maelisSeed', 'druid', 'amulet', 'bloom', 'lifesteal', { durationBp: 15000 }),
};

export const UNIQUE_IDS = Object.keys(UNIQUES).sort() as UniqueId[];

/** The unique piece of a class for a slot. */
export const uniqueFor = (cls: HeroClass, slot: UniqueSlot): UniqueDef => UNIQUE_IDS.map((id) => UNIQUES[id]).find((d) => d.cls === cls && d.slot === slot) as UniqueDef;

/**
 * The dragon of the Burn (docs/UNICAS.md §4, docs/LORE.md), the saga's last foe and the hardest fight in the game. It is
 * fought in a clearing of its own (`field`): no node and no waves, only what its roar calls up. Its six glass scales
 * let little through; frost fills the glass's cold and a dry blow breaks one, fire closes them. In the sky it circles
 * over the old harpoons, which bring it down; on the ground it bites, sweeps and eats the absent friends' spectres; at
 * 55 % it goes up once more, and from 30 % its fury glasses the clearing in from the rim. Every number here.
 */
export const DRAGON = {
  /** The clearing: its radius in hexes (all of it open ground, no roads). */
  field: { radius: 10 },
  /** Cadence of its blows by phase (sky, ground, fury). */
  every: [sec(4.5), sec(4), sec(2.75)],
  /** In the sky: how far from the middle it circles, and how fast (1/8192 of a turn a tick: a lap in ~28 s). */
  orbit: 4500,
  arc: 24,
  breath: { marks: 6, spacing: 900, radius: 800, warn: sec(1.5), damage: 95 },
  sweep: { radius: 2200, warn: sec(1.5), damage: 85 },
  /** A marked spectre within `range` is eaten (once each), mending it `healBp` of its life. */
  devour: { range: 2600, healBp: 1000 },
  /** Inked nightmares it roars up on landing by itself and in fury, by heroes counted (rounded down; 1 to 8). */
  roarAdds: [0, 3, 4, 5, 6, 6, 6, 6, 6],
  furySpeedBp: 13000,
  cleaveRadius: 900,
  /** Its fury: one eruption under every hero along with the breath. */
  furyEruptions: true,
  /**
   * The six scales (§4.3): with all whole `wholeBp` of a blow gets through, each one broken `perBp` more. The cold one
   * asks, in reference frost lances (the Frost Lance's damage at the fight's level), by heroes counted (1 to 5+,
   * interpolated). After `still` without frost it drops `dropBp` of what it asks every second. `closeBp` of a lance
   * of fire closes a broken one.
   */
  scales: { n: 6, wholeBp: 1000, perBp: 1500, lances: [0, 40000, 40000, 60000, 60000, 90000], still: sec(7), dropBp: 500, closeBp: 15000 },
  /**
   * The old harpoons on the rim (§4.4): `n` of them (`ring` from the middle), their lane `lane` wide, lit `warn`
   * before it crosses; a hero `lever` at the lever (within `reach`) fires it. It reloads by itself in `reload`; one
   * stuck holds `hold`, slowing it to `slowBp`; two at once bring it down. A spectre within `spectre` of a free
   * harpoon goes for its lever, only with another already in. The harpoon's own blow, as bp of its life.
   */
  harpoons: { n: 3, ring: 9000, lever: sec(1), reach: 700, leverOff: 1100, lane: 1500, warn: sec(3), reload: sec(12), hold: sec(25), slowBp: 5000, spectre: 3000, hitBp: 50 },
  /** Brought down: it lies `ticks`, `breaks` scales broken by the fall; meanwhile a dry blow breaks one a `every` at most. */
  down: { ticks: sec(5), breaks: 2, every: sec(1) },
  /** Alone in the sky it lands after `first` (the second time, `second`); it goes up again at `riseAtBp`, once. */
  sky: { first: sec(90), second: sec(60) },
  riseAtBp: 5500,
  furyAtBp: 3000,
  /** Before its fury there is a clock too: past it, it rages with whatever life it has. */
  clock: sec(12 * 60),
  /**
   * Its fury (§4.4): every `every` the rim turns to burning glass a hex further in, its ground red `warn` before; it
   * burns `damage` every `pulse` whoever stands on it. A broken scale closes with the heat every `close`. In the
   * beacon's light the glass takes `beaconBp` as long. At `max` the glass covers it all.
   */
  fury: { every: sec(20), warn: sec(4), pulse: 6, damage: 30, close: sec(20), beaconBp: 20000, max: sec(240) },
  /** Its life fell below 30 % in a try: a scar, a scale that never closes again (two at most), §4.7. */
  scars: { max: 2 },
  /** A spectre counts this much of a hero (bp) for its life, the cold a scale asks, its roar and BIG_PARTY. */
  spectreBp: 6500,
  /** Its life on top of the act's table, the level and the chapter's toughness (bp): the fight's own measure (§4.10). */
  hpBp: 15000,
  /**
   * Who comes to the last fight, by what the group did (docs/QUESTS.md): Ser Gaspar's spectre if they
   * listened to him, Maelis if she learned the truth, and the village's carts of bolts (more turrets) if
   * Odna trusts the garrison this much.
   */
  allies: { gaspar: 'gaspar.heard', maelis: 'maelis.knows', villageAff: 2, villageTurrets: 2, powerBp: 9000 },
  /**
   * What La Quemadura's chapters bring to this fight (§4.6), each from its chapter done: the map marks its fire and the
   * glass earlier (`warnBp` as long); the plans, a fourth harpoon Hilde rebuilt; the diary, its cracked scale (broken
   * from the start, never closing); the beacon, its light on the middle (`beaconR`: no spectre in it is eaten, the
   * glass takes longer in it); and the saga done, Orla with her wayfarers' bow (her arrows are dry blows).
   */
  burn: { warnBp: 16000, beaconR: 3200, orla: 'orla.ayuda' },
  /** If it never falls (§4.12): the armour, given anyway once it has its two scars and two more rounds lost after them. */
  planB: { rounds: 2 },
};
